import { defineStore } from 'pinia'

const STORAGE_KEY = 'ordersFilters'

interface IOrdersFilters {
  tierIDs: string[]
  insuranceCustomerAccountIDs: string[]
  /** Hide orders from internal accounts (MIL-252). */
  hideInternal: boolean
  /** Only insurance cases still missing their skadesnummer. */
  missingInsuranceNumberOnly: boolean
}

const DEFAULT_FILTERS: IOrdersFilters = {
  tierIDs: [],
  insuranceCustomerAccountIDs: [],
  hideInternal: false,
  missingInsuranceNumberOnly: false,
}

export const useOrdersFilterStore = defineStore('ordersFilter', {
  state: () => ({
    tierIDs: [] as string[],
    insuranceCustomerAccountIDs: [] as string[],
    hideInternal: false,
    missingInsuranceNumberOnly: false,
    isLoaded: false,
  }),

  getters: {
    hasActiveFilters(state): boolean {
      return (
        state.tierIDs.length > 0 ||
        state.insuranceCustomerAccountIDs.length > 0 ||
        state.hideInternal ||
        state.missingInsuranceNumberOnly
      )
    },
  },

  actions: {
    loadFilters() {
      if (this.isLoaded) return
      this.isLoaded = true

      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsed: unknown = JSON.parse(stored)
          if (parsed && typeof parsed === 'object') {
            const { tierIDs, insuranceCustomerAccountIDs, hideInternal, missingInsuranceNumberOnly } =
              parsed as Partial<IOrdersFilters>
            this.tierIDs = Array.isArray(tierIDs)
              ? tierIDs.filter((x): x is string => typeof x === 'string')
              : []
            // Filters saved before Partner was renamed (MIL-402) carry the list as `partnerIDs`.
            const storedInsuranceCustomerAccountIDs: unknown =
              insuranceCustomerAccountIDs ?? (parsed as { partnerIDs?: unknown }).partnerIDs
            this.insuranceCustomerAccountIDs = Array.isArray(storedInsuranceCustomerAccountIDs)
              ? storedInsuranceCustomerAccountIDs.filter((x): x is string => typeof x === 'string')
              : []
            // Absent in filters persisted before MIL-252 — default rather than reject,
            // so an existing saved filter set is not discarded wholesale.
            this.hideInternal = typeof hideInternal === 'boolean' ? hideInternal : false
            this.missingInsuranceNumberOnly =
              typeof missingInsuranceNumberOnly === 'boolean' ? missingInsuranceNumberOnly : false
            return
          }
        }
      } catch {}

      this.applyDefaults()
    },

    persist() {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          tierIDs: this.tierIDs,
          insuranceCustomerAccountIDs: this.insuranceCustomerAccountIDs,
          hideInternal: this.hideInternal,
          missingInsuranceNumberOnly: this.missingInsuranceNumberOnly,
        } satisfies IOrdersFilters)
      )
    },

    setTierIDs(ids: string[]) {
      this.tierIDs = ids
      this.persist()
    },

    setInsuranceCustomerAccountIDs(ids: string[]) {
      this.insuranceCustomerAccountIDs = ids
      this.persist()
    },

    setHideInternal(value: boolean) {
      this.hideInternal = value
      this.persist()
    },

    setMissingInsuranceNumberOnly(value: boolean) {
      this.missingInsuranceNumberOnly = value
      this.persist()
    },

    applyDefaults() {
      this.tierIDs = [...DEFAULT_FILTERS.tierIDs]
      this.insuranceCustomerAccountIDs = [...DEFAULT_FILTERS.insuranceCustomerAccountIDs]
      this.hideInternal = DEFAULT_FILTERS.hideInternal
      this.missingInsuranceNumberOnly = DEFAULT_FILTERS.missingInsuranceNumberOnly
    },

    clearFilters() {
      this.applyDefaults()
      this.persist()
    },
  },
})
